import { VscsClientEventMap, VscsServerEventMap } from "@poscon/shared-types/vscs";
import { IncomingWtEvent, posconApi, setConnection, setSocketConnected } from "@poscon/shared-frontend";
import { Manager, Socket } from "socket.io-client";
import type { DefaultEventsMap } from "@socket.io/component-emitter";
import { isAction } from "@reduxjs/toolkit";
import { ArtccId } from "@poscon/shared-types";
import { ControllerSectorId, PosconRole } from "@poscon/shared-types/poscon";
import { RootStore } from "./redux/store";
import { env } from "./env";
import { listen, Event } from "@tauri-apps/api/event";
import { invoke } from "@tauri-apps/api/core";

let store: RootStore;

const ERAM_SERVER_URL = env.VITE_ERAM_SERVER_URL;

type IncomingEventsMap = VscsClientEventMap & DefaultEventsMap;

const encoder = new TextEncoder();

export class VscsHubConnection {
    private manager: Manager<IncomingEventsMap, VscsServerEventMap> | null = null;
    private _socket: Socket<IncomingEventsMap, VscsServerEventMap> | null = null;

    get socket() {
        return this._socket;
    }

    accessToken: string | null = null;
    isActive = false;
    connected = false;
    usingWt = false;
    private _sectorId: ControllerSectorId | null = null;

    get sectorId() {
        return this._sectorId;
    }

    async handleWtEvent(e: Event<IncomingWtEvent>) {
        const payload = e.payload;
        const data = JSON.parse(payload.data);
        const request_id = payload.data_request_id;
        const eventHandler = this.eventHandlers[payload.event_name];
        // @ts-ignore
        const response = eventHandler?.([data]);
        if (response && typeof request_id === "number") {
            await invoke("nas_event_response", encoder.encode(JSON.stringify(response)), { headers: { id: request_id.toString(10) } });
        }
    }


    async initialize(artccId: ArtccId, sectorId: ControllerSectorId | null, role: PosconRole) {
        this.usingWt = window.__TAURI__ && await invoke<boolean>("supports_wt").catch(() => false);
        if (this.usingWt) {
            await this.initializeWt();
        } else {
            await this.initializeSio(artccId, sectorId, role);
        }

    }

    async initializeWt() {
        const _unlisten = await listen<IncomingWtEvent>("nas_event", async (e) => {
            await this.handleWtEvent(e);
        });

        listen("wt_connected", async () => {
            this.onConnect();
        });
        listen("wt_disconnected", () => {
            this.onDisconnect();
        });

    }

    async initializeSio(artccId: ArtccId, sectorId: ControllerSectorId | null, role: PosconRole) {
        this.accessToken = await posconApi.getAtcAccessToken();

        if (this.manager === null) {
            const manager = new Manager<IncomingEventsMap, VscsServerEventMap>(ERAM_SERVER_URL, {
                transports: ["websocket", "webtransport"],
                path: "/socket.io",
                reconnection: true,
                reconnectionAttempts: 12,
                reconnectionDelay: 5e3,
                autoConnect: false,
            });
            this.manager = manager;

            const namespace = `/eram.${artccId}`;

            const socket = manager.socket(namespace, {
                auth: {
                    token: this.accessToken,
                    client: "VSCS",
                    role,
                    artccId,
                    sectorId,
                },
            });

            socket.on("connect_error", (err) => {
                console.error("vscs hub connection error", err.message);
            });
            socket.on("connect", () => {
                console.log(`vscs connected via ${this.socket?.io.engine.transport.name}`);
                this.onConnect();
            });
            socket.on("disconnect", () => {
                this.onDisconnect();
            });

            this._socket = socket;
        }
    }

    onConnect() {
        this.connected = true;
        store.dispatch(setSocketConnected(true));
        this.emit("ready", "VSCS");
    }

    onDisconnect() {
        this.connected = false;
        store.dispatch(setSocketConnected(false));
        store.dispatch(setConnection(null));
    }

    emit<K extends keyof VscsServerEventMap>(event: K, ...args: Parameters<VscsServerEventMap[K]>) {
        this._socket?.emit(event, ...args);
    }

    onStoreInit(s: RootStore) {
        store = s;
        this.registerEventHandler("receiveConnectionStatus", (connection) => {
            this._sectorId = connection.sectorId;
            this.isActive = connection.isActive;
            store.dispatch(setConnection(connection));
        });
        this.registerEventHandler("receiveVscsUiAction", (action) => {
            if (isAction(action)) {
                store.dispatch({ ...action, meta: { forwarded: true } });
            }
        });
    }

    private eventHandlers: {
        [K in keyof IncomingEventsMap]?: (...args: Parameters<IncomingEventsMap[K]>) => unknown;
    } = {};

    registerEventHandler<K extends keyof IncomingEventsMap>(
        event: K,
        callback: (...args: Parameters<IncomingEventsMap[K]>) => unknown,
    ) {
        this.eventHandlers[event] = callback;
        this._socket?.on(event as string & symbol, (...args: unknown[]) => {
            // @ts-ignore
            callback(...args);
        });
    }

    pressButton(btnId: number) {
        this.emit("pressButton", btnId);
    }

    sendUiAction(action: unknown) {
        this.emit("receiveVscsUiAction", action);
    }

    async connect() {
        return new Promise<void>((resolve, reject) => {
            if (this._socket) {
                if (this._socket.connected) { resolve(); return; }
                this._socket.once("connect", () => resolve());
                this._socket.connect();
            } else {
                reject("NO_SOCKET");
            }
        });
    }

    disconnect() {
        this._socket?.disconnect();
        store?.dispatch(setConnection(null));
    }
}

export const vscsHubConnection = new VscsHubConnection();

export const injectStore = (s: RootStore) => {
    store = s;
    vscsHubConnection.onStoreInit(s);
};
